import { useState, useEffect, useRef } from 'react';
import { ClipboardList, ArrowRight, ArrowLeft, Link as LinkIcon, FileText, Type, AlignLeft, Loader2, Plus, X, DollarSign, ListChecks, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Select } from '@/components/ui/Select';
import { LabConfiguratorShell } from './LabConfiguratorShell';
import { ProductEconomicsInput } from './ProductEconomicsInput';
import { bundleEconomics, calculateProductEconomics, parseProductEconomics, type ProductEconomics } from '@/lib/product-economics';
import { ReviewsFileInput } from './ReviewsFileInput';
import { getLabTextFileError } from './labTextFileInput';

export interface UserInputOption {
  value: string;
  label: string;
  description?: string;
}

export interface UserInputVisibilityCondition {
  field: string;
  equals?: string | string[];
  notEquals?: string | string[];
}

export interface UserInputField {
  type: 'user_input';
  name: string;
  inputType?: 'text' | 'textarea' | 'link' | 'file' | 'packages' | 'select';
  label?: string;
  helperText?: string;
  required?: boolean;
  placeholder?: string;
  defaultValue?: string;
  options?: Array<UserInputOption | string>;
  visibleWhen?: UserInputVisibilityCondition[];
  panelTitle?: string;
  panelDescription?: string;
  ctaLabel?: string;
  backLabel?: string;
  allowMultiple?: boolean;
  autoFillFrom?: string;
  screenKey?: string;
  fileAccept?: string;
  fallbackForSource?: string;
}

export interface SourceCheckState {
  status: 'checking' | 'completed' | 'failed' | 'invalid';
  checked: number;
  total: number;
  error?: string;
  preview?: string;
}

interface InputPanelProps {
  field?: UserInputField;
  fields?: UserInputField[];
  value?: string;
  values?: Record<string, string>;
  onChange?: (value: string) => void;
  onFieldChange?: (fieldName: string, value: string) => void;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onBack?: () => void;
  onClose?: () => void;
  loading?: boolean;
  sourceChecks?: Record<string, SourceCheckState>;
  sourceCheckKeys?: string[];
}

const INPUT_TYPE_ICONS: Record<string, typeof Type> = {
  text: Type,
  textarea: AlignLeft,
  link: LinkIcon,
  file: FileText,
  packages: DollarSign,
  select: ListChecks,
};

interface PricePackageInput {
  economics?: ProductEconomics;
  name: string;
  oldPrice: string;
  newPrice: string;
}

const emptyPackageRow = (): PricePackageInput => ({
  name: '1 sztuka',
  oldPrice: '',
  newPrice: '',
});

function parsePackageRows(rawValue: string): PricePackageInput[] {
  if (!rawValue.trim()) return [emptyPackageRow()];

  try {
    const parsed = JSON.parse(rawValue);
    if (Array.isArray(parsed)) {
      const rows = parsed
        .map(item => {
          if (!item || typeof item !== 'object') return null;
          const record = item as Record<string, unknown>;
          return {
            name: String(record.name || ''),
            oldPrice: String(record.oldPrice || ''),
            newPrice: String(record.newPrice || ''),
            ...(record.economics && typeof record.economics === 'object' ? { economics: record.economics as ProductEconomics } : {}),
          };
        })
        .filter((row): row is PricePackageInput => Boolean(row));
      return rows.length > 0 ? rows : [emptyPackageRow()];
    }
  } catch {
    // Older free-text values are kept in the first row so the user can normalize them.
  }

  return [{ name: rawValue, oldPrice: '', newPrice: '' }];
}

function serializePackageRows(rows: PricePackageInput[]): string {
  const normalized = rows
    .map(row => ({
      name: row.name.trim(),
      oldPrice: row.oldPrice.trim(),
      newPrice: row.newPrice.trim(),
      ...(row.economics ? { economics: row.economics } : {}),
    }))
    .filter(row => row.name || row.oldPrice || row.newPrice);

  return normalized.length > 0 ? JSON.stringify(normalized) : '';
}

export const MULTI_SEP = '\n---MULTI---\n';

export function InputPanel({ field, fields, value = '', values, onChange, onFieldChange, stepIndex, totalSteps, onNext, onBack, onClose, loading, sourceChecks = {}, sourceCheckKeys = [] }: InputPanelProps) {
  const screenFields = fields && fields.length > 0 ? fields : field ? [field] : [];
  const screenSignature = screenFields.map(inputField => inputField.name).join('|');
  const primaryField = screenFields[0];
  const Icon = INPUT_TYPE_ICONS[primaryField?.inputType || 'text'] || Type;
  const isLastStep = stepIndex === totalSteps - 1;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const shouldAutoFocus = screenFields.length === 1;

  // Multi-value state
  const [multiValuesByField, setMultiValuesByField] = useState<Record<string, string[]>>({});
  const [fileErrorsByField, setFileErrorsByField] = useState<Record<string, string>>({});

  // Sync multiValues from value prop
  useEffect(() => {
    const next: Record<string, string[]> = {};
    for (const inputField of screenFields) {
      if (!inputField.allowMultiple) continue;
      const currentValue = values ? (values[inputField.name] || '') : value;
      const parts = currentValue ? currentValue.split(MULTI_SEP) : [''];
      next[inputField.name] = parts.length > 0 ? parts : [''];
    }
    setMultiValuesByField(next);
  }, [
    screenFields.map(inputField => `${inputField.name}:${inputField.allowMultiple ? 'multi' : 'single'}`).join('|'),
    screenFields.map(inputField => values ? (values[inputField.name] || '') : value).join('|'),
  ]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [stepIndex, screenSignature]);

  const emitChange = (fieldName: string, nextValue: string) => {
    if (onFieldChange) {
      onFieldChange(fieldName, nextValue);
      return;
    }
    onChange?.(nextValue);
  };

  const updateMultiValues = (fieldName: string, newVals: string[]) => {
    setMultiValuesByField(prev => ({ ...prev, [fieldName]: newVals }));
    emitChange(fieldName, newVals.join(MULTI_SEP));
  };

  const handleMultiChange = (fieldName: string, index: number, val: string) => {
    const current = multiValuesByField[fieldName] || [''];
    const next = [...current];
    next[index] = val;
    updateMultiValues(fieldName, next);
  };

  const addMultiRow = (fieldName: string) => {
    updateMultiValues(fieldName, [...(multiValuesByField[fieldName] || ['']), '']);
  };

  const removeMultiRow = (fieldName: string, index: number) => {
    const current = multiValuesByField[fieldName] || [''];
    if (current.length <= 1) return;
    const next = current.filter((_, i) => i !== index);
    updateMultiValues(fieldName, next);
  };

  const valueForField = (inputField: UserInputField) => (
    values ? (values[inputField.name] || '') : value
  );

  const sourceCheckPending = sourceCheckKeys.some(key => sourceChecks[key]?.status === 'checking');
  const sourceCheckInvalid = sourceCheckKeys.some(key => sourceChecks[key]?.status === 'invalid');
  const isRequiredNow = (inputField: UserInputField) => Boolean(inputField.required);
  const canProceed = !sourceCheckPending && !sourceCheckInvalid && screenFields.every(inputField => {
    if (inputField.name === 'product_unit_cost') {
      const economics = parseProductEconomics(valueForField(inputField));
      if (economics.purchasePrice.trim() && !calculateProductEconomics(economics)) return false;
    }
    if (inputField.inputType === 'packages') {
      const invalidCost = parsePackageRows(valueForField(inputField)).some(row => {
        const data = bundleEconomics(values?.product_unit_cost, row.economics);
        return !Number.isInteger(Number(data.saleQuantity)) || Number(data.saleQuantity) < 1 || (data.purchasePrice.trim() && !calculateProductEconomics(data));
      });
      if (invalidCost) return false;
    }
    if (!isRequiredNow(inputField)) return true;
    if (inputField.inputType === 'packages') {
      const currentValue = valueForField(inputField).trim();
      return Boolean(currentValue) && parsePackageRows(currentValue).some(row => row.name.trim() || row.oldPrice.trim() || row.newPrice.trim());
    }
    if (inputField.allowMultiple) {
      return (multiValuesByField[inputField.name] || ['']).some(v => v.trim());
    }
    return valueForField(inputField).trim();
  });

  // Cmd/Ctrl+Enter to submit
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canProceed) {
        onNext();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [values, value, canProceed]);

  const handleFileRead = (inputField: UserInputField, file: File, multiIndex?: number) => {
    const extensionError = file.type.startsWith('image/')
      ? null
      : getLabTextFileError(file.name, '');
    if (extensionError) {
      setFileErrorsByField(prev => ({ ...prev, [inputField.name]: extensionError }));
      if (multiIndex !== undefined) {
        handleMultiChange(inputField.name, multiIndex, '');
      } else {
        emitChange(inputField.name, '');
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      const fileError = file.type.startsWith('image/')
        ? null
        : getLabTextFileError(file.name, result);
      if (fileError) {
        setFileErrorsByField(prev => ({ ...prev, [inputField.name]: fileError }));
        if (multiIndex !== undefined) {
          handleMultiChange(inputField.name, multiIndex, '');
        } else {
          emitChange(inputField.name, '');
        }
        return;
      }

      setFileErrorsByField(prev => {
        const next = { ...prev };
        delete next[inputField.name];
        return next;
      });
      const text = file.type.startsWith('image/')
        ? JSON.stringify({
          kind: 'image_reference',
          fileName: file.name,
          mimeType: file.type,
          size: file.size,
          dataUrl: result,
        }, null, 2)
        : result;
      if (multiIndex !== undefined) {
        handleMultiChange(inputField.name, multiIndex, text);
      } else {
        emitChange(inputField.name, text);
      }
    };
    reader.onerror = () => {
      setFileErrorsByField(prev => ({
        ...prev,
        [inputField.name]: 'Nie udało się odczytać pliku. Wybierz go ponownie.',
      }));
    };
    if (file.type.startsWith('image/')) {
      reader.readAsDataURL(file);
    } else {
      reader.readAsText(file);
    }
  };

  // Button labels
  const nextLabel = isLastStep
    ? (primaryField?.ctaLabel || 'Generuj bazę wiedzy')
    : 'Dalej';
  const backLabelText = primaryField?.backLabel || 'Wstecz';

  const inputClasses = 'w-full px-4 py-3 bg-surface-hover/80 border border-border rounded-xl text-text-primary text-sm focus:outline-none focus:border-accent-primary/50 placeholder:text-text-primary/20 backdrop-blur-sm';

  const renderSourceCheck = (sourceKey: string) => {
    const check = sourceChecks[sourceKey];
    if (!check) return null;
    if (check.status === 'checking') {
      return (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-accent-primary" role="status">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Odczytuję źródło automatycznie...
        </p>
      );
    }
    if (check.status === 'completed') {
      return (
        <div className="mt-2 rounded-lg border border-success/20 bg-success/5 px-3 py-2 text-xs text-success">
          <p className="flex items-center gap-1.5 font-medium">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Treść strony pobrana technicznie{check.total > 1 ? ` (${check.checked}/${check.total})` : ''}
          </p>
          {check.preview && <p className="mt-1 line-clamp-2 text-text-muted">{check.preview}</p>}
          <p className="mt-1 text-text-muted">Analiza AI rozpocznie się dopiero po zatwierdzeniu briefu.</p>
        </div>
      );
    }
    return (
      <div className={`mt-2 rounded-lg border px-3 py-2 text-xs ${check.status === 'failed' ? 'border-danger/25 bg-danger/5 text-danger' : 'border-warning/25 bg-warning/5 text-warning'}`} role="alert">
        <p className="flex items-center gap-1.5 font-medium">
          <AlertTriangle className="h-3.5 w-3.5" />
          {check.status === 'failed' ? 'Nie udało się odczytać źródła' : 'Adres źródła jest nieprawidłowy'}
        </p>
        {check.error && <p className="mt-1 text-text-muted">{check.error}</p>}
        {check.status === 'failed' && <p className="mt-1 text-text-secondary">Możesz wkleić treść w polu poniżej. Ten link nie zablokuje analizy pozostałych materiałów.</p>}
      </div>
    );
  };

  const renderSingleInput = (inputField: UserInputField, val: string, onValChange: (v: string) => void, index?: number) => {
    if (inputField.name === 'product_unit_cost') {
      return <ProductEconomicsInput mode="purchase" value={val} onChange={data => onValChange(JSON.stringify(data))} />;
    }
    if (inputField.inputType === 'select') {
      const options = (inputField.options || []).map(option => (
        typeof option === 'string'
          ? { value: option, label: option, description: '' }
          : option
      ));
      if (options.some(option => option.description)) {
        return (
          <div className="grid gap-3 md:grid-cols-2">
            {options.map(option => {
              const selected = val === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onValChange(option.value)}
                  className={`min-h-[108px] rounded-xl border p-4 text-left transition-all ${
                    selected
                      ? 'border-accent-primary/70 bg-accent-primary/15 shadow-[0_0_0_1px_rgba(77,151,255,0.28)]'
                      : 'border-border bg-surface-hover/50 hover:border-accent-primary/35 hover:bg-surface-hover/70'
                  }`}
                >
                  <span className="block text-sm font-semibold text-text-primary">{option.label}</span>
                  {option.description && (
                    <span className="mt-2 block text-xs leading-relaxed text-text-secondary">
                      {option.description}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        );
      }
      return (
        <Select
          value={val}
          onChange={e => onValChange(e.target.value)}
          className={inputClasses}
          placeholder={inputField.placeholder || 'Wybierz opcję'}
        >
          <option value="" disabled>
            {inputField.placeholder || 'Wybierz opcję'}
          </option>
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      );
    }
    if (inputField.inputType === 'textarea') {
      return (
        <textarea
          value={val}
          onChange={e => onValChange(e.target.value)}
          placeholder={inputField.placeholder}
          rows={4}
          className={`${inputClasses} resize-none`}
          autoFocus={shouldAutoFocus && (index === undefined || index === 0)}
        />
      );
    }
    if (inputField.inputType === 'file' && inputField.autoFillFrom === 'reviewsCsv') {
      return <ReviewsFileInput value={val} onChange={onValChange} />;
    }
    if (inputField.inputType === 'file') {
      return (
        <div>
          <input
            type="file"
            accept={inputField.fileAccept || '.csv,.txt,.json,.md'}
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) handleFileRead(inputField, file, index);
            }}
            className={`${inputClasses} file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:bg-surface-hover/60 file:text-text-primary/70 file:text-xs file:cursor-pointer`}
          />
          {val && (
            <p className="text-xs text-success mt-1">
              Wczytano {val.length > 120 ? `${val.length} znaków` : 'plik'}
            </p>
          )}
          {fileErrorsByField[inputField.name] && (
            <p className="text-xs text-danger mt-1" role="alert">
              {fileErrorsByField[inputField.name]}
            </p>
          )}
        </div>
      );
    }
    return (
      <input
        type={inputField.inputType === 'link' ? 'url' : 'text'}
        value={val}
        onChange={e => onValChange(e.target.value)}
        placeholder={inputField.placeholder || (inputField.inputType === 'link' ? 'https://...' : '')}
        className={inputClasses}
        autoFocus={shouldAutoFocus && (index === undefined || index === 0)}
      />
    );
  };

  const renderPackageInput = (inputField: UserInputField, val: string) => {
    const rows = parsePackageRows(val);

    const updateRows = (nextRows: PricePackageInput[]) => {
      emitChange(inputField.name, serializePackageRows(nextRows));
    };

    const updateRow = (index: number, patch: Partial<PricePackageInput>) => {
      const next = rows.map((row, rowIndex) => (
        rowIndex === index ? { ...row, ...patch } : row
      ));
      updateRows(next);
    };

    const addRow = () => {
      updateRows([...rows, { name: `${rows.length + 1} sztuki`, oldPrice: '', newPrice: '', economics: { ...parseProductEconomics(values?.product_unit_cost), saleQuantity: String(rows.length + 1) } }]);
    };

    const removeRow = (index: number) => {
      if (rows.length <= 1) return;
      updateRows(rows.filter((_, rowIndex) => rowIndex !== index));
    };

    return (
      <div className="space-y-3">
        {rows.map((row, index) => (
          <div key={index} className="rounded-xl border border-border bg-surface-hover/40 p-3">
            <ProductEconomicsInput mode="bundle" purchaseSource={values?.product_unit_cost} value={row.economics} onChange={economics => updateRow(index, { economics })} />
            <div className="mt-3 grid grid-cols-1 md:grid-cols-[1.2fr_1fr_1fr_auto] gap-3 items-end">
              <div>
                <label className="block text-[11px] text-text-primary/40 mb-1">Pakiet / ilość</label>
                <input
                  type="text"
                  value={row.name}
                  onChange={e => updateRow(index, { name: e.target.value })}
                  placeholder="1 sztuka"
                  className={inputClasses}
                  autoFocus={shouldAutoFocus && index === 0}
                />
              </div>
              <div>
                <label className="block text-[11px] text-text-primary/40 mb-1">Cena regularna</label>
                <input
                  type="text"
                  value={row.oldPrice}
                  onChange={e => updateRow(index, { oldPrice: e.target.value })}
                  placeholder="XXX zł"
                  className={inputClasses}
                />
              </div>
              <div>
                <label className="block text-[11px] text-text-primary/40 mb-1">Cena sprzedaży</label>
                <input
                  type="text"
                  value={row.newPrice}
                  onChange={e => updateRow(index, { newPrice: e.target.value })}
                  placeholder="XXX zł"
                  className={inputClasses}
                />
              </div>
              {rows.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeRow(index)}
                  className="mb-3 p-1.5 text-text-muted hover:text-danger transition-colors justify-self-start md:justify-self-auto"
                  title="Usuń pakiet"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={addRow}
          className="flex items-center gap-1.5 text-xs text-accent-primary/70 hover:text-accent-primary transition-colors mt-1"
        >
          <Plus className="w-3.5 h-3.5" />
          Dodaj pakiet
        </button>
      </div>
    );
  };

  return (
    <LabConfiguratorShell
      title={primaryField?.panelTitle || primaryField?.label || 'Dane wstępne'}
      description={primaryField?.panelDescription}
      icon={<ClipboardList className="w-6 h-6" />}
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      onClose={onClose}
      closeDisabled={loading}
      scrollRef={scrollRef}
      bodyKey={screenSignature}
      bodyClassName="pb-8"
      maxWidthClassName={screenFields.length > 1 ? 'max-w-5xl' : undefined}
      footer={(
        <div className="flex items-center justify-between gap-4">
          <button
            onClick={onNext}
            disabled={!canProceed || loading}
            className="flex items-center gap-2 px-8 py-3 bg-accent-primary/20 text-accent-primary rounded-xl text-sm font-semibold hover:bg-accent-primary/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed border border-accent-primary/30"
          >
            {loading || sourceCheckPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ArrowRight className="w-4 h-4" />
            )}
            {sourceCheckPending ? 'Sprawdzam źródło...' : nextLabel}
          </button>
          {onBack && stepIndex > 0 && (
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-6 py-3 text-text-primary/40 hover:text-text-primary/70 text-sm transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {backLabelText}
            </button>
          )}
        </div>
      )}
    >
      <div className={screenFields.length > 1 ? 'grid grid-cols-1 gap-5 md:grid-cols-2' : 'space-y-6'}>
        {screenFields.map((inputField, fieldIndex) => {
          const FieldIcon = INPUT_TYPE_ICONS[inputField.inputType || 'text'] || Icon;
          const currentValue = valueForField(inputField);
          const multiValues = multiValuesByField[inputField.name] || [''];
          const shouldSpanFullWidth = inputField.inputType === 'textarea'
            || inputField.inputType === 'file'
            || inputField.inputType === 'packages';
          return (
            <div key={inputField.name} className={shouldSpanFullWidth ? 'md:col-span-2' : undefined}>
              <label className="flex items-center gap-2 text-sm font-medium text-text-primary mb-1.5">
                <FieldIcon className="w-4 h-4 text-text-primary/40" />
                {inputField.name === 'product_unit_cost' ? 'Wspólne warunki zakupu' : inputField.label || inputField.name}
                {isRequiredNow(inputField) && <span className="text-danger text-xs">*</span>}
              </label>
              {inputField.helperText && (
                <p className="text-xs text-text-primary/30 mb-2">{inputField.name === 'product_unit_cost' ? 'Wpisz cenę zakupu raz. Koszty pakietów przeliczymy według liczby sprzedawanych sztuk.' : inputField.helperText}</p>
              )}

              {inputField.inputType === 'packages' ? (
                renderPackageInput(inputField, currentValue)
              ) : inputField.allowMultiple ? (
                <div className="space-y-2">
                  {multiValues.map((val, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <div className="flex-1">
                        {renderSingleInput(inputField, val, (v) => handleMultiChange(inputField.name, idx, v), fieldIndex === 0 ? idx : undefined)}
                      </div>
                      {multiValues.length > 1 && (
                        <button
                          onClick={() => removeMultiRow(inputField.name, idx)}
                          className="mt-3 p-1 text-text-muted hover:text-danger transition-colors shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => addMultiRow(inputField.name)}
                    className="flex items-center gap-1.5 text-xs text-accent-primary/70 hover:text-accent-primary transition-colors mt-1"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Dodaj kolejne
                  </button>
                </div>
              ) : (
                renderSingleInput(inputField, currentValue, (nextValue) => emitChange(inputField.name, nextValue), fieldIndex === 0 ? undefined : 1)
              )}
              {inputField.inputType === 'link' && renderSourceCheck(inputField.name)}
            </div>
          );
        })}
      </div>
    </LabConfiguratorShell>
  );
}
